import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid } from '@web/utils';
import { Observable } from 'rxjs';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorService } from '@web/common';
import { AdminDictionaryCountryDetailsFormComponent } from '../../components/admin-dictionary-country-details-form/admin-dictionary-country-details-form.component';
import { AdminDictionaryCountryDetailsForm } from '../../models/admin-dictionary-country-details-form';
import { AdminDictionaryCountryDataSourceService } from '../../services/admin-dictionary-country-data-source.service';

@Component({
  selector: 'app-admin-dictionary-country-edit',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    AlertsComponent,
    AdminDictionaryCountryDetailsFormComponent,
  ],
  templateUrl: './admin-dictionary-country-edit.component.html',
  styleUrl: './admin-dictionary-country-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminDictionaryCountryEditComponent {
  /* prettier-ignore */
  private readonly adminDictionaryDataSource = inject(AdminDictionaryCountryDataSourceService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output();

  protected data$!: Observable<AdminDictionaryCountryDetailsForm>;

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    effect(() => this.configureDataSource());
  }

  public handleSubmitClickEvent(data: AdminDictionaryCountryDetailsForm): void {
    this.adminDictionaryDataSource
      .updateByFormData(this.id(), data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.submitSuccessEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private configureDataSource(): void {
    this.data$ = this.adminDictionaryDataSource.fetchDetailsFormById(this.id());
    this.changeDetectorRef.detectChanges();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
